import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Save, ShieldCheck, RefreshCw,
  BarChart3, Table as TableIcon, Search, Image as ImageIcon,
  ArrowUp, ArrowDown, ExternalLink, Flame, LayoutPanelTop, Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "asian_picks_admin_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const emptyPrediction = {
  date: new Date().toISOString().slice(0, 10),
  match: "",
  competition: "",
  pick: "",
  odds: 1.95,
  status: "win",
};

// =============================================================================
// Login screen
// =============================================================================
function LoginScreen({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Signed in.");
      onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-emerald-700/30 blur-[120px]" />
      <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold-500/20 blur-[140px]" />

      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="relative w-full max-w-sm p-8 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/10"
      >
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center border border-gold-500/40">
            <ShieldCheck className="w-5 h-5 text-gold-400" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg text-white leading-none">
              Asian<span className="gold-text">Picks</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/80 mt-0.5">
              Admin Panel
            </div>
          </div>
        </div>
        <h1 className="font-display font-bold text-2xl text-white mb-1">Sign in</h1>
        <p className="text-sm text-white/60 mb-6">Restricted area. Authorized users only.</p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="username" className="text-white/80">Username</Label>
            <Input
              id="username"
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-white/80">Password</Label>
            <Input
              id="password"
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/30"
            />
          </div>
          <Button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-gold-500 hover:bg-gold-400 text-ink-950 font-semibold"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// =============================================================================
// Prediction form dialog
// =============================================================================
function PredictionDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(initial || emptyPrediction);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial || emptyPrediction);
  }, [initial, open]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        match: form.match.trim(),
        competition: form.competition.trim(),
        pick: form.pick.trim(),
        odds: parseFloat(form.odds),
        status: form.status,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/predictions/${initial.id}`, payload, { headers: authHeader() });
        toast.success("Prediction updated.");
      } else {
        await axios.post(`${API}/admin/predictions`, payload, { headers: authHeader() });
        toast.success("Prediction added.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prediction-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit prediction" : "Add prediction"}</DialogTitle>
          <DialogDescription>All picks are stored as Asian Handicap selections.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="p-date">Date</Label>
              <Input id="p-date" data-testid="p-date" type="date" required value={form.date} onChange={setField("date")} />
            </div>
            <div>
              <Label htmlFor="p-odds">Odds</Label>
              <Input id="p-odds" data-testid="p-odds" type="number" step="0.01" min="1.01" required value={form.odds} onChange={setField("odds")} />
            </div>
          </div>
          <div>
            <Label htmlFor="p-match">Match (e.g. "Real Madrid vs Barcelona")</Label>
            <Input id="p-match" data-testid="p-match" required value={form.match} onChange={setField("match")} />
          </div>
          <div>
            <Label htmlFor="p-competition">Competition</Label>
            <Input id="p-competition" data-testid="p-competition" required value={form.competition} onChange={setField("competition")} />
          </div>
          <div>
            <Label htmlFor="p-pick">AH Pick (e.g. "Barcelona -0.5 AH")</Label>
            <Input id="p-pick" data-testid="p-pick" required value={form.pick} onChange={setField("pick")} />
          </div>
          <div>
            <Label>Status</Label>
            <div className="mt-1.5 flex gap-2" data-testid="p-status-group">
              {["win", "loss", "draw"].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`p-status-${s}`}
                  onClick={() => setForm((f) => ({ ...f, status: s }))}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-all capitalize ${
                    form.status === s
                      ? "bg-emerald-700 border-emerald-700 text-white"
                      : "bg-white border-ink-200 text-ink-700 hover:border-emerald-700/50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="p-save"
              type="submit"
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add prediction"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Predictions tab
// =============================================================================
function PredictionsPanel({ onDataChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/predictions`, { headers: authHeader() });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load predictions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      p.match.toLowerCase().includes(q) ||
      p.competition.toLowerCase().includes(q) ||
      p.pick.toLowerCase().includes(q) ||
      p.date.includes(q)
    );
  }, [items, search]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/predictions/${deleteTarget.id}`, { headers: authHeader() });
      toast.success("Prediction deleted.");
      setDeleteTarget(null);
      await load();
      onDataChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-predictions-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Predictions</h2>
          <p className="text-sm text-ink-500">
            {items.length} entries · Any change updates the live archive instantly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
            <Input
              data-testid="admin-pred-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-9 sm:w-64"
            />
          </div>
          <Button
            data-testid="admin-refresh"
            variant="outline"
            size="icon"
            onClick={load}
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            data-testid="admin-add-prediction"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-2xl border border-ink-200 shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr className="text-left font-mono uppercase tracking-wider text-[11px] text-ink-500">
              <th className="px-4 py-3 whitespace-nowrap">Date</th>
              <th className="px-4 py-3 whitespace-nowrap">Match</th>
              <th className="px-4 py-3 whitespace-nowrap">Competition</th>
              <th className="px-4 py-3 whitespace-nowrap">AH Pick</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Odds</th>
              <th className="px-4 py-3 whitespace-nowrap text-center">Status</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-pred-tbody">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-500">No predictions match.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`admin-pred-row-${p.id}`} className="border-b border-ink-100 hover:bg-emerald-700/[0.03]">
                <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{p.date}</td>
                <td className="px-4 py-3 whitespace-nowrap font-semibold text-ink-900">{p.match}</td>
                <td className="px-4 py-3 whitespace-nowrap text-ink-500">{p.competition}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 rounded-md bg-emerald-700/10 text-emerald-800 font-mono font-semibold text-xs">{p.pick}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right font-mono font-semibold">{p.odds.toFixed(2)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-center"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      data-testid={`admin-edit-${p.id}`}
                      variant="ghost" size="icon"
                      onClick={() => { setEditing(p); setDialogOpen(true); }}
                      aria-label="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`admin-delete-${p.id}`}
                      variant="ghost" size="icon"
                      onClick={() => setDeleteTarget(p)}
                      aria-label="Delete"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PredictionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={() => { load(); onDataChanged?.(); }}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this prediction?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  This will permanently remove <strong>{deleteTarget.match}</strong>{" "}
                  ({deleteTarget.date}) from the live archive. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Stats tab
// =============================================================================
const STAT_FIELDS = [
  { key: "total_tips", label: "Total tips (lifetime)", suffix: "" },
  { key: "wins", label: "Wins", suffix: "" },
  { key: "losses", label: "Losses", suffix: "" },
  { key: "draws", label: "Draws", suffix: "" },
  { key: "win_rate", label: "Win rate", suffix: "%" },
  { key: "years_experience", label: "Years experience", suffix: "yr" },
  { key: "members", label: "Members", suffix: "+" },
];

function StatsPanel({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { headers: authHeader() });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load stats.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const updateField = (key, patch) => {
    setData((d) => ({
      ...d,
      config: {
        ...d.config,
        [key]: { ...d.config[key], ...patch },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await axios.put(`${API}/admin/stats`, data.config, { headers: authHeader() });
      setData(updated);
      toast.success("Stats updated — homepage reflects it now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="text-ink-500 p-8">Loading…</div>;
  }

  return (
    <div data-testid="admin-stats-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Homepage statistics</h2>
          <p className="text-sm text-ink-500">
            Auto-calculated from your archive by default. Flip "Manual override" for any stat you want to display a custom number for.
          </p>
        </div>
        <Button
          data-testid="admin-stats-save"
          onClick={save}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div
        data-testid="stats-auto-explainer"
        className="flex items-start gap-3 p-4 rounded-xl border border-gold-500/30 bg-gold-500/[0.06] text-ink-700 text-sm leading-relaxed"
      >
        <Info className="w-5 h-5 mt-0.5 shrink-0 text-gold-600" />
        <p>
          <span className="font-semibold text-ink-900">Why "Auto" is smaller than "Manual":</span>{" "}
          the Auto value is computed from the <strong>last 50 tips</strong> currently in your public archive.
          Your Manual override values represent the <strong>full lifetime</strong> track record (1,114+ tips going back to 2023).
          Keep the lifetime stats on <strong>Manual</strong> unless you specifically want the homepage to reflect only the most recent 50-tip window.
        </p>
      </div>

      <TooltipProvider delayDuration={200}>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {STAT_FIELDS.map(({ key, label, suffix }) => {
          const cfg = data.config[key];
          const auto = data.computed[key];
          const effective = cfg.manual && cfg.override != null ? cfg.override : auto;
          return (
            <div
              key={key}
              data-testid={`stat-card-${key}`}
              className="p-5 rounded-2xl bg-white border border-ink-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-500">{label}</div>
                  <div className="mt-1 font-display font-bold text-3xl text-ink-900">
                    {typeof effective === "number" ? effective.toLocaleString("en-US") : effective}
                    {suffix && <span className="text-lg text-ink-500 ml-1">{suffix}</span>}
                  </div>
                  <div className="mt-1 text-[11px] text-ink-500 font-mono">
                    Auto-computed from archive: <span className="text-ink-900 font-semibold">{auto}{suffix}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor={`sw-${key}`} className="text-xs text-ink-500 whitespace-nowrap">
                    {cfg.manual ? "Manual" : "Auto"}
                  </Label>
                  <Switch
                    id={`sw-${key}`}
                    data-testid={`stat-manual-${key}`}
                    checked={cfg.manual}
                    onCheckedChange={(v) => updateField(key, { manual: v })}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        data-testid={`stat-info-${key}`}
                        aria-label={`About ${label} auto vs manual`}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-ink-500 hover:text-emerald-700 hover:bg-emerald-700/10 transition-colors"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[240px] text-xs leading-relaxed">
                      <strong>Auto</strong> reads the value from the last 50 tips in your archive.
                      <strong> Manual</strong> displays whatever number you type below — use this for lifetime totals that predate the current archive.
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div>
                <Label className="text-xs text-ink-500">Override value</Label>
                <Input
                  data-testid={`stat-override-${key}`}
                  type="number"
                  step={key === "win_rate" ? "0.1" : "1"}
                  disabled={!cfg.manual}
                  value={cfg.override ?? ""}
                  onChange={(e) =>
                    updateField(key, {
                      override: e.target.value === "" ? null : parseFloat(e.target.value),
                    })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          );
        })}
      </div>
      </TooltipProvider>
    </div>
  );
}

// =============================================================================
// Free Tip tab
// =============================================================================
// =============================================================================
// Slot Banners — list-style, per-slot add/remove/reorder (same layout as
// "Advertising Banners"). Two fixed positions: predictions_bottom and
// free_tips_above_tip.
// =============================================================================
const SLOT_CONFIG = [
  {
    key: "free_tips_above_tip",
    title: "Above Free Tip",
    description: "Above the Free Tip of the Day card on /free-tips.",
    width: 190,
    height: 45,
  },
  {
    key: "free_tips_below_tip",
    title: "Below Free Tip",
    description: "Below the Free Tip of the Day card on /free-tips.",
    width: 190,
    height: 45,
  },
  {
    key: "free_tip_card_middle",
    title: "Inside Free Tip — between Tip/Odds and Analysis",
    description: "Inside the Free Tip card, two banners per row.",
    width: 420,
    height: 80,
  },
  {
    key: "free_tip_card_bottom",
    title: "Inside Free Tip — below Analysis",
    description: "Inside the Free Tip card, two banners per row.",
    width: 420,
    height: 80,
  },
];

const emptySlotBanner = (slot) => ({
  slot: slot.key,
  title: "",
  link_url: "",
  image_url: "",
  width: slot.width,
  height: slot.height,
});
function SlotBannerFormDialog({ open, onOpenChange, initial, slot, onSaved }) {
  const [form, setForm] = useState(initial || emptySlotBanner(slot));
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;
  const dims = `${slot.width} × ${slot.height}`;

  useEffect(() => {
    setForm(initial || emptySlotBanner(slot));
  }, [initial, slot, open]);

  const setField = (k) => (e) => {
    const v = e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      toast.error("File too large. Please upload an image under 300 KB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        slot: slot.key,
        title: form.title.trim(),
        link_url: form.link_url.trim(),
        image_url: (form.image_url || "").trim(),
        width: slot.width,
        height: slot.height,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/slot-banners/${initial.id}`, payload, { headers: authHeader() });
        toast.success("Banner updated.");
      } else {
        await axios.post(`${API}/admin/slot-banners`, payload, { headers: authHeader() });
        toast.success("Banner added.");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="slot-banner-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit banner" : "Add banner"}</DialogTitle>
          <DialogDescription>Recommended size: {dims} px.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="sb-title">Title</Label>
            <Input id="sb-title" data-testid="sb-title" required value={form.title} onChange={setField("title")} placeholder="China Tips" />
          </div>
          <div>
            <Label htmlFor="sb-link">Link URL</Label>
            <Input id="sb-link" data-testid="sb-link" type="url" required value={form.link_url} onChange={setField("link_url")} placeholder="https://partner-site.com" />
          </div>
          <div>
            <Label htmlFor="sb-img">Image URL</Label>
            <Input id="sb-img" data-testid="sb-img" type="text" required value={form.image_url} onChange={setField("image_url")} placeholder="https://res.cloudinary.com/.../banner.gif" />
            <div className="mt-2 flex items-center gap-2">
              <label
                htmlFor="sb-file"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-300 bg-white text-ink-700 text-xs font-semibold hover:bg-ink-50 cursor-pointer"
              >
                <ImageIcon className="w-3.5 h-3.5" /> Upload from computer
              </label>
              <input
                id="sb-file"
                data-testid="sb-file"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={handleFile}
              />
              <span className="text-[11px] text-ink-500 font-mono">PNG / JPG / GIF · ≤ 300 KB · exactly {dims} px</span>
            </div>
          </div>
          {form.image_url && (
            <div className="p-3 rounded-md bg-ink-50 border border-ink-200">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500 mb-2">Preview ({dims})</div>
              <img
                src={form.image_url}
                alt="preview"
                style={{ width: `${slot.width}px`, height: `${slot.height}px`, maxWidth: "100%" }}
                className="object-cover rounded ring-1 ring-ink-200 block"
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="sb-save" type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add banner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SlotBannerGroup({ slot, items, onChanged }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const move = async (id, direction) => {
    try {
      await axios.post(
        `${API}/admin/slot-banners/${id}/move?direction=${direction}`,
        {},
        { headers: authHeader() }
      );
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/slot-banners/${deleteTarget.id}`, { headers: authHeader() });
      toast.success("Banner deleted.");
      setDeleteTarget(null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid={`slot-group-${slot.key}`} className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-mono uppercase tracking-widest text-xs text-ink-500">
            {slot.title} — {items.length} {items.length === 1 ? "banner" : "banners"}
          </h3>
          <p className="text-xs text-ink-500">{slot.description}</p>
        </div>
        <Button
          data-testid={`slot-add-${slot.key}`}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add banner
        </Button>
      </div>

      {items.length === 0 && (
        <div className="p-6 text-center text-ink-500 bg-white rounded-2xl border border-ink-200 text-sm">
          No banners in this slot yet. Click <strong>Add banner</strong> to place one here.
        </div>
      )}

      {items.map((b, idx) => (
        <div
          key={b.id}
          data-testid={`slot-banner-row-${b.id}`}
          className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-white border border-ink-200 shadow-sm"
        >
          <div className="hidden sm:flex flex-col items-center justify-center w-10 shrink-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500">#{idx + 1}</div>
            <div className="text-[10px] font-mono text-ink-500">of {items.length}</div>
          </div>

          <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded overflow-hidden ring-1 ring-ink-200 hover:ring-emerald-700 transition-all">
            <img
              src={b.image_url}
              alt={b.title}
              style={{ width: `${slot.width}px`, height: `${slot.height}px`, maxWidth: "40vw" }}
              className="object-cover block"
            />
          </a>

          <div className="flex-1 min-w-0">
            <div className="font-semibold text-ink-900 truncate">{b.title}</div>
            <div className="text-xs text-ink-500 flex items-center gap-1 truncate">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span className="truncate">{b.link_url}</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mt-0.5">
              {slot.width} × {slot.height} px
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" data-testid={`slot-banner-up-${b.id}`} onClick={() => move(b.id, "up")} disabled={idx === 0} aria-label="Move up">
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" data-testid={`slot-banner-down-${b.id}`} onClick={() => move(b.id, "down")} disabled={idx === items.length - 1} aria-label="Move down">
              <ArrowDown className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" data-testid={`slot-banner-edit-${b.id}`} onClick={() => { setEditing(b); setDialogOpen(true); }} aria-label="Edit">
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" data-testid={`slot-banner-delete-${b.id}`} onClick={() => setDeleteTarget(b)} aria-label="Delete" className="text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}

      <SlotBannerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        slot={slot}
        onSaved={onChanged}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="slot-banner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{deleteTarget?.title}</strong> from the {slot.title} slot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SlotBannersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorText("");
    try {
      const { data } = await axios.get(`${API}/admin/slot-banners`, { headers: authHeader() });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      const msg = `Could not load slot banners${status ? ` (HTTP ${status})` : ""}: ${detail}`;
      setErrorText(msg);
      toast.error(msg);
      // Do NOT wipe existing items on transient errors, but keep the panel usable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-ink-500">Loading…</div>;

  return (
    <div data-testid="admin-slot-banners-panel" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Slot Banners</h2>
          <p className="text-sm text-ink-500">
            Four fixed positions. Each slot is managed independently — add, reorder, or delete without affecting the others.
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={load} aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {errorText && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="slot-banners-load-error">
          <strong>Load error:</strong> {errorText}
        </div>
      )}

      {SLOT_CONFIG.map((s) => (
        <SlotBannerGroup
          key={s.key}
          slot={s}
          items={items.filter((b) => b.slot === s.key)}
          onChanged={load}
        />
      ))}
    </div>
  );
}

// =============================================================================
// Free Tip of the Day
// =============================================================================
function PricingPanel() {
  const PLAN_META = { starter: "Starter — 15 AH Tips", pro: "Pro — 30 AH Tips", vip: "VIP — 50 AH Tips" };
  const [plans, setPlans] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/pricing`, { headers: authHeader() })
      .then(({ data }) => setPlans(data.plans))
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not load pricing."));
  }, []);

  const update = (key, patch) =>
    setPlans((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { plans: plans.map((p) => ({ ...p, price: parseFloat(p.price) || 0 })) };
      const { data } = await axios.put(`${API}/admin/pricing`, payload, { headers: authHeader() });
      setPlans(data.plans);
      toast.success("Pricing updated — Pricing page reflects it now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!plans) return <div className="p-8 text-ink-500">Loading…</div>;

  return (
    <div data-testid="admin-pricing-panel" className="space-y-5 max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Pricing Plans</h2>
          <p className="text-sm text-ink-500">
            Edit price, currency and availability of each offer on the public <span className="font-mono">/pricing</span> page.
          </p>
        </div>
        <Button
          data-testid="pricing-save"
          onClick={save}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="space-y-4">
        {plans.map((p) => (
          <div
            key={p.key}
            data-testid={`pricing-plan-${p.key}`}
            className="p-5 rounded-2xl bg-white border border-ink-200 shadow-sm"
          >
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="font-semibold text-ink-900">{PLAN_META[p.key]}</div>
              <div className="flex items-center gap-2">
                <Switch
                  id={`plan-available-${p.key}`}
                  data-testid={`pricing-available-${p.key}`}
                  checked={!p.unavailable}
                  onCheckedChange={(v) => update(p.key, { unavailable: !v })}
                />
                <Label htmlFor={`plan-available-${p.key}`} className="text-sm">
                  {p.unavailable ? (
                    <span className="text-red-600 font-medium">Not Available</span>
                  ) : (
                    <span className="text-emerald-700 font-medium">Available</span>
                  )}
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`plan-price-${p.key}`}>Price</Label>
                <Input
                  id={`plan-price-${p.key}`}
                  data-testid={`pricing-price-${p.key}`}
                  type="number"
                  min="0"
                  step="1"
                  value={p.price}
                  onChange={(e) => update(p.key, { price: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor={`plan-currency-${p.key}`}>Currency</Label>
                <select
                  id={`plan-currency-${p.key}`}
                  data-testid={`pricing-currency-${p.key}`}
                  value={p.currency}
                  onChange={(e) => update(p.key, { currency: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FreeTipPanel() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/free-tip`, { headers: authHeader() });
      setForm(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load free tip.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k) => (e) => {
    const v = e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!form.enabled,
        match: form.match.trim(),
        tip: String(form.tip).trim(),
        odds: parseFloat(form.odds),
        analysis: (form.analysis || "").trim(),
      };
      const { data } = await axios.put(`${API}/admin/free-tip`, payload, { headers: authHeader() });
      setForm(data);
      toast.success("Free tip updated — Free Tips page reflects it now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <div className="p-8 text-ink-500">Loading…</div>;

  return (
    <div data-testid="admin-free-tip-panel" className="space-y-5 max-w-3xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Free Tip of the Day</h2>
          <p className="text-sm text-ink-500">
            Edit the match, tip, odds and analysis shown on the public <span className="font-mono">/free-tips</span> page.
          </p>
        </div>
        <Button
          data-testid="free-tip-save"
          onClick={save}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="p-6 rounded-2xl bg-white border border-ink-200 shadow-sm space-y-5">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-ink-50">
          <Switch
            id="free-tip-enabled"
            data-testid="free-tip-enabled"
            checked={!!form.enabled}
            onCheckedChange={(v) => setField("enabled")(v)}
          />
          <Label htmlFor="free-tip-enabled" className="text-sm">
            {form.enabled ? "Visible on Free Tips page" : "Hidden — tip won't be shown"}
          </Label>
        </div>

        <div>
          <Label htmlFor="ft-match">Match</Label>
          <Input
            id="ft-match"
            data-testid="ft-match"
            required
            value={form.match}
            onChange={setField("match")}
            placeholder="Bayern vs Real Madrid"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ft-tip">Tip</Label>
            <Input
              id="ft-tip"
              data-testid="ft-tip"
              required
              value={form.tip}
              onChange={setField("tip")}
              placeholder="1"
            />
            <p className="mt-1 text-[11px] text-ink-500 font-mono">e.g. "1", "Over 2.5", "Bayern -0.5 AH"</p>
          </div>
          <div>
            <Label htmlFor="ft-odds">Odds</Label>
            <Input
              id="ft-odds"
              data-testid="ft-odds"
              type="number"
              step="0.01"
              min="1.01"
              required
              value={form.odds}
              onChange={setField("odds")}
              placeholder="1.60"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="ft-analysis">Analysis</Label>
          <textarea
            id="ft-analysis"
            data-testid="ft-analysis"
            rows={6}
            value={form.analysis || ""}
            onChange={setField("analysis")}
            placeholder="Explain your reasoning for the tip — form, injuries, tactics, price value…"
            className="w-full px-3 py-2 rounded-md border border-ink-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-[11px] text-ink-500 font-mono">Line breaks preserved on the page.</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Banners tab
// =============================================================================
const emptyBanner = {
  title: "",
  link_url: "",
  image_url: "",
  width: 170,
  height: 50,
};

function BannerDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(initial || emptyBanner);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial || emptyBanner); }, [initial, open]);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        link_url: form.link_url.trim(),
        image_url: form.image_url.trim(),
        width: parseInt(form.width, 10) || 170,
        height: parseInt(form.height, 10) || 50,
      };
      if (isEdit) {
        await axios.put(`${API}/admin/banners/${initial.id}`, payload, { headers: authHeader() });
        toast.success("Banner updated.");
      } else {
        await axios.post(`${API}/admin/banners`, payload, { headers: authHeader() });
        toast.success("Banner added.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="banner-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit banner" : "Add banner"}</DialogTitle>
          <DialogDescription>Small 170×50 ad banners shown around the Free Tips section.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="b-title">Title (advertiser name)</Label>
            <Input id="b-title" data-testid="b-title" required value={form.title} onChange={setField("title")} placeholder="China Tips" />
          </div>
          <div>
            <Label htmlFor="b-link">Link URL</Label>
            <Input id="b-link" data-testid="b-link" type="url" required value={form.link_url} onChange={setField("link_url")} placeholder="https://china-tips.com/" />
          </div>
          <div>
            <Label htmlFor="b-img">Image URL (GIF, PNG, JPG)</Label>
            <Input id="b-img" data-testid="b-img" type="url" required value={form.image_url} onChange={setField("image_url")} placeholder="https://res.cloudinary.com/.../banner.gif" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="b-w">Width (px)</Label>
              <Input id="b-w" data-testid="b-w" type="number" min="1" max="2000" required value={form.width} onChange={setField("width")} />
            </div>
            <div>
              <Label htmlFor="b-h">Height (px)</Label>
              <Input id="b-h" data-testid="b-h" type="number" min="1" max="1000" required value={form.height} onChange={setField("height")} />
            </div>
          </div>
          {form.image_url && (
            <div className="p-3 rounded-md bg-ink-50 border border-ink-200">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500 mb-2">Preview</div>
              <img
                src={form.image_url}
                alt="preview"
                style={{ width: `${form.width}px`, height: `${form.height}px` }}
                className="object-cover rounded ring-1 ring-ink-200"
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="b-save" type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white">
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add banner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BannersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/banners`, { headers: authHeader() });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load banners.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (id, direction) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/banners/${id}/move?direction=${direction}`,
        {},
        { headers: authHeader() }
      );
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/banners/${deleteTarget.id}`, { headers: authHeader() });
      toast.success("Banner deleted.");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div data-testid="admin-banners-panel" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink-900">Advertising Banners</h2>
          <p className="text-sm text-ink-500">
            {items.length} banners · Displayed above &amp; below the Free Tips section. Use the arrows to reorder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            data-testid="admin-add-banner"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add banner
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {loading && <div className="p-8 text-center text-ink-500 bg-white rounded-2xl border border-ink-200">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-ink-500 bg-white rounded-2xl border border-ink-200">
            No banners yet. Add your first one to have it appear on the Free Tips page.
          </div>
        )}
        {items.map((b, idx) => (
          <div
            key={b.id}
            data-testid={`admin-banner-row-${b.id}`}
            className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl bg-white border border-ink-200 shadow-sm"
          >
            {/* Order badge */}
            <div className="hidden sm:flex flex-col items-center justify-center w-10 shrink-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500">#{idx + 1}</div>
              <div className="text-[10px] font-mono text-ink-500">of {items.length}</div>
            </div>

            {/* Preview */}
            <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded overflow-hidden ring-1 ring-ink-200 hover:ring-emerald-700 transition-all">
              <img
                src={b.image_url}
                alt={b.title}
                style={{ width: `${b.width}px`, height: `${b.height}px` }}
                className="object-cover block"
              />
            </a>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink-900 truncate">{b.title}</div>
              <div className="text-xs text-ink-500 flex items-center gap-1 truncate">
                <ExternalLink className="w-3 h-3 shrink-0" />
                <span className="truncate">{b.link_url}</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mt-0.5">
                {b.width} × {b.height} px
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost" size="icon"
                data-testid={`banner-up-${b.id}`}
                onClick={() => move(b.id, "up")}
                disabled={idx === 0}
                aria-label="Move up"
              >
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                data-testid={`banner-down-${b.id}`}
                onClick={() => move(b.id, "down")}
                disabled={idx === items.length - 1}
                aria-label="Move down"
              >
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                data-testid={`banner-edit-${b.id}`}
                onClick={() => { setEditing(b); setDialogOpen(true); }}
                aria-label="Edit"
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                data-testid={`banner-delete-${b.id}`}
                onClick={() => setDeleteTarget(b)}
                aria-label="Delete"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <BannerDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="banner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>This will permanently remove <strong>{deleteTarget.title}</strong> from the Free Tips page. This action cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Admin shell
// =============================================================================
export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [tab, setTab] = useState("predictions");
  const [refreshKey, setRefreshKey] = useState(0);

  // Verify token on mount
  useEffect(() => {
    document.title = "Admin — Asian Picks";
    if (!authed) return;
    axios
      .get(`${API}/admin/me`, { headers: authHeader() })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, [authed]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    toast.success("Signed out.");
  };

  if (!authed) return <LoginScreen onLoggedIn={() => setAuthed(true)} />;

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-ink-50">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-ink-950 text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center border border-gold-500/40">
              <ShieldCheck className="w-5 h-5 text-gold-400" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">
                Asian<span className="gold-text">Picks</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/80 mt-0.5">
                Admin Panel
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-white/60 hover:text-gold-400 px-3"
            >
              View site →
            </a>
            <Button
              data-testid="admin-logout"
              onClick={logout}
              variant="outline"
              size="sm"
              className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="inline-flex p-1 rounded-lg bg-white border border-ink-200 shadow-sm">
          <button
            data-testid="tab-predictions"
            onClick={() => setTab("predictions")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "predictions" ? "bg-emerald-700 text-white" : "text-ink-700 hover:bg-ink-50"
            }`}
          >
            <TableIcon className="w-4 h-4" /> Predictions
          </button>
          <button
            data-testid="tab-stats"
            onClick={() => setTab("stats")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "stats" ? "bg-emerald-700 text-white" : "text-ink-700 hover:bg-ink-50"
            }`}
          >
            <BarChart3 className="w-4 h-4" /> Statistics
          </button>
          <button
            data-testid="tab-slot-banners"
            onClick={() => setTab("slot-banners")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "slot-banners" ? "bg-emerald-700 text-white" : "text-ink-700 hover:bg-ink-50"
            }`}
          >
            <LayoutPanelTop className="w-4 h-4" /> Slot Banners
          </button>
          <button
            data-testid="tab-free-tip"
            onClick={() => setTab("free-tip")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "free-tip" ? "bg-emerald-700 text-white" : "text-ink-700 hover:bg-ink-50"
            }`}
          >
            <Flame className="w-4 h-4" /> Free Tip
          </button>
          <button
            data-testid="tab-pricing"
            onClick={() => setTab("pricing")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === "pricing" ? "bg-emerald-700 text-white" : "text-ink-700 hover:bg-ink-50"
            }`}
          >
            <Tag className="w-4 h-4" /> Pricing
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === "predictions" && (
          <PredictionsPanel onDataChanged={() => setRefreshKey((k) => k + 1)} />
        )}
        {tab === "stats" && <StatsPanel refreshKey={refreshKey} />}
        {tab === "slot-banners" && <SlotBannersPanel />}
        {tab === "free-tip" && <FreeTipPanel />}
        {tab === "pricing" && <PricingPanel />}
      </main>
    </div>
  );
}
